import React, { useEffect, useState, useCallback, useContext } from "react";
import Statistics from "./Statistics";
import DateBox from "../../../../common/DateBox/DateBox";
import { formatDate } from "../../../../utils/validation.util";
import TargetCard from "./TargetHealth/TargetCard";
import EditTarget from "./TargetHealth/EditTarget";
import { ApiContext } from "../../../../context/MyContext";
import { Toaster } from "../../../../common/Toaster/Toaster";
import { fetchUserTargetPlanSleep } from "../../../../service/useranalytics/FetchSleepService";
import { fetchUserTargetPlanSteps } from "../../../../service/useranalytics/FetchStepsService";
import { fetchUserTargetPlanWater } from "../../../../service/useranalytics/FetchWaterService";
import { modifyTargetPlanSleep } from "../../../../service/useranalytics/ModifyTargetSleep";
import { modifyTargetPlanSteps } from "../../../../service/useranalytics/ModifyTargetStepService";
import { modifyTargetPlanWater } from "../../../../service/useranalytics/ModifyTargetWaterService";
import { useUtils } from "../../../../context/UtilsContext";
import {
  prefetchUserProfile,
  peekProfileDigest,
  resolveProfileEventTypeId,
} from "../../../../utils/userProfileDigest";

// `variant` is optional and backward-compatible. The outer container is
// already fluid ("w-full"); callers that need containment (e.g. the narrow
// client-focus rail) wrap this component in an overflow-x-auto div instead.
const UserAnalytics = ({ profileId, variant: _variant }) => {
  const [visibleDates, setVisibleDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));
  const [isTargetModalOpen, setIsTargetModalOpen] = useState(false);
  const [targetData, setTargetData] = useState({
    weight: 0,
    steps: 0,
    sleep: 0,
    sleepStart: "",
    sleepEnd: "",
    water: 0,
  });
  const [combinedResults, setCombinedResults] = useState({
    sleep_id: "",
    sleep_split_id: "", // Add this to track the split ID
    sleep_type_id: "", // Add this to track the sleep type ID
    step_id: "",
    water_id: "",
    weight_id: "",
  });
  const [userName, setUserName] = useState("");
  const { get, post } = useContext(ApiContext);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [loading, setLoading] = useState(false);
  const { sessionUtils } = useUtils();
  const eventTypeId = resolveProfileEventTypeId(sessionUtils);

  // Fetch all target plans
  const getTargetInfo = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch all three target plans in parallel
      const [sleepResult, stepsResult, waterResult] = await Promise.all([
        fetchUserTargetPlanSleep(get, eventTypeId, profileId),
        fetchUserTargetPlanSteps(get, eventTypeId, profileId),
        fetchUserTargetPlanWater(get, eventTypeId, profileId),
      ]);

      // Create new objects to ensure fresh state
      const newTargetData = {
        weight: 58,
        steps: 10000,
        sleep: 8,
        sleepStart: "22:00",
        sleepEnd: "06:00",
        water: 2000,
      };

      const combinedRes = {
        sleep_id: "",
        sleep_split_id: "",
        sleep_type_id: "",
        step_id: "",
        water_id: "",
        weight_id: "",
      };

      if (sleepResult.success && sleepResult.data) {
        const sleepTarget =
          sleepResult.data.both?.[0] ||
          sleepResult.data.created?.[0] ||
          sleepResult.data.assigned?.[0];

        // 🔍 DEBUG: Log the raw sleep target data

        if (sleepTarget?.sleep_hours) {
          combinedRes.sleep_id = sleepTarget.id;
          newTargetData.sleep = sleepTarget.sleep_hours;

          // ✅ FIX: Extract sleep start and end times from the API response
          // Assuming the API returns splits array with sleep_start and sleep_end
          if (sleepTarget.splits && sleepTarget.splits.length > 0) {
            const split = sleepTarget.splits[0];

            // Store split ID and sleep type ID for updates
            combinedRes.sleep_split_id = split.id;
            combinedRes.sleep_type_id = split.sleep_type_id;


            if (split.sleep_start) {
              // Extract only the time part from ISO datetime (e.g., "2025-10-10T22:00:15.652Z" -> "22:00")
              const timeMatch = split.sleep_start.match(/T(\d{2}):(\d{2})/);
              if (timeMatch) {
                newTargetData.sleepStart = `${timeMatch[1]}:${timeMatch[2]}`;
              }
            }
            if (split.sleep_end) {
              // Extract only the time part from ISO datetime
              const timeMatch = split.sleep_end.match(/T(\d{2}):(\d{2})/);
              if (timeMatch) {
                newTargetData.sleepEnd = `${timeMatch[1]}:${timeMatch[2]}`;
              }
            }

            
          }
        }
      }

      if (stepsResult.success && stepsResult.data) {
        const stepsTarget =
          stepsResult.data.both?.[0] ||
          stepsResult.data.created?.[0] ||
          stepsResult.data.assigned?.[0];
        if (stepsTarget?.step_count) {
          combinedRes.step_id = stepsTarget.id;
          newTargetData.steps = stepsTarget.step_count;
        }
      }

      if (waterResult.success && waterResult.data) {
        const waterTarget =
          waterResult.data.both?.[0] ||
          waterResult.data.created?.[0] ||
          waterResult.data.assigned?.[0];
        if (waterTarget?.water_quantity) {
          combinedRes.water_id = waterTarget.id;
          newTargetData.water = waterTarget.water_quantity;
        }
      }


      // Update both states
      setCombinedResults(combinedRes);
      setTargetData(newTargetData);

      return { success: true };
    } catch (error) {
      console.error("Error fetching target info:", error);
      setToastMessage("Failed to load target information");
      setToastType("error");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, [get, eventTypeId, profileId]);

  useEffect(() => {
    getTargetInfo();
  }, [getTargetInfo]);

  // The name only labels the Set Target modal, so a failed lookup stays silent.
  useEffect(() => {
    let cancelled = false;
    prefetchUserProfile(eventTypeId, profileId).then(() => {
      if (!cancelled) setUserName(peekProfileDigest(profileId)?.name || "");
    });
    return () => {
      cancelled = true;
    };
  }, [eventTypeId, profileId]);

  const updateDateRange = useCallback(() => {
    const today = new Date();
    const dates = [];
    const range = [-12, 0];
    for (let i = range[0]; i <= range[1]; i++) {
      const newDate = new Date(today);
      newDate.setDate(today.getDate() + i);
      dates.push(newDate);
    }
    setVisibleDates(dates);
  }, []);

  const handleOnSelectDate = (date) => setSelectedDate(date);

  useEffect(() => {
    updateDateRange();
    window.addEventListener("resize", updateDateRange);
    return () => window.removeEventListener("resize", updateDateRange);
  }, [updateDateRange]);

  const handleSetTarget = () => {
    setIsTargetModalOpen((prev) => !prev);
  };

  const handleTargetSubmit = async (updatedTargetData) => {

    try {
      setLoading(true);

      // Prepare payloads for each target plan
      const promises = [];

      // ✅ FIX: Check if sleep hours OR sleep times have changed
      const sleepChanged =
        updatedTargetData.target_sleep_hrs !== targetData.sleep ||
        updatedTargetData.target_sleep_start !== targetData.sleepStart ||
        updatedTargetData.target_sleep_end !== targetData.sleepEnd;

      if (sleepChanged) {
        // ✅ FIX: Format times correctly - use current date with the time, keep as UTC
        const today = new Date().toISOString().split("T")[0]; // Get YYYY-MM-DD
        const sleepStartISO = `${today}T${updatedTargetData.target_sleep_start}:00.000Z`;
        const sleepEndISO = `${today}T${updatedTargetData.target_sleep_end}:00.000Z`;

        const sleepPayload = {
          event_type_id: eventTypeId,
          assigned_to_id: profileId,
          id: combinedResults.sleep_id,
          operation: "UPDATE",
          sleep_hours: updatedTargetData.target_sleep_hrs,
          splits: [
            {
              operation: "UPDATE",
              id: combinedResults.sleep_split_id, // Use the stored split ID
              sleep_type_id: combinedResults.sleep_type_id, // Use the stored sleep type ID
              sleep_start: sleepStartISO,
              sleep_end: sleepEndISO,
            },
          ],
        };
        promises.push(modifyTargetPlanSleep(post, sleepPayload));
      }

      if (updatedTargetData.target_step_count !== targetData.steps) {
        const stepsPayload = {
          event_type_id: eventTypeId,
          assigned_to_id: profileId,
          id: combinedResults.step_id,
          operation: "UPDATE",
          step_count: updatedTargetData.target_step_count,
        };
        promises.push(modifyTargetPlanSteps(post, stepsPayload));
      }

      if (updatedTargetData.target_quantity_ml !== targetData.water) {
        const waterPayload = {
          event_type_id: eventTypeId,
          assigned_to_id: profileId,
          id: combinedResults.water_id,
          operation: "UPDATE",
          water_quantity: updatedTargetData.target_quantity_ml,
        };
        promises.push(modifyTargetPlanWater(post, waterPayload));
      }

      // If no changes, just close the modal
      if (promises.length === 0) {
        setToastMessage("No changes to update");
        setToastType("info");
        setIsTargetModalOpen(false);
        setLoading(false);
        return;
      }

      // Execute all updates in parallel
      const results = await Promise.all(promises);

      // Check if all updates were successful
      const allSuccessful = results.every((result) => result.success);

      if (allSuccessful) {
        // Create a completely new object to trigger React re-render
        const newTargetData = {
          weight: updatedTargetData.target_weight_kg || targetData.weight,
          steps: updatedTargetData.target_step_count || targetData.steps,
          sleep: updatedTargetData.target_sleep_hrs || targetData.sleep,
          sleepStart:
            updatedTargetData.target_sleep_start || targetData.sleepStart,
          sleepEnd: updatedTargetData.target_sleep_end || targetData.sleepEnd,
          water: updatedTargetData.target_quantity_ml || targetData.water,
        };

        // Update state with new object reference
        setTargetData(newTargetData);

        // Show success message BEFORE closing modal
        setToastMessage("Target plans updated successfully");
        setToastType("success");

        // Close modal after a brief delay to allow state to update
        setTimeout(() => {
          setIsTargetModalOpen(false);
        }, 100);

        // Refresh in background to ensure data consistency
        getTargetInfo();
      } else {
        const failedUpdates = results.filter((r) => !r.success);
        setToastMessage(failedUpdates[0]?.message || "Some updates failed");
        setToastType("error");
        setIsTargetModalOpen(false);
      }
    } catch (error) {
      console.error("Error updating target plans:", error);
      setToastMessage("Error updating target plans");
      setToastType("error");
      setIsTargetModalOpen(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  return (
    <div className="w-full app-bg">
      {toastMessage && <Toaster message={toastMessage} type={toastType} />}
      <div className="text-center">
        <div>
          <TargetCard
            targetData={targetData}
            onSetTarget={handleSetTarget}
            loading={loading}
          />
        </div>
        <div className="flex overflow-x-auto py-2 mb-6">
          {visibleDates.map((date, index) => (
            <DateBox
              key={index}
              date={date.getDate()}
              month={date.toLocaleString("default", { month: "short" })}
              year={date.getFullYear()}
              selected={selectedDate === formatDate(date)}
              OnSelect={() => handleOnSelectDate(formatDate(date))}
            />
          ))}
        </div>
        <div className="rounded-xl shadow-md bg-light-gray overflow-hidden p-4">
          <Statistics profileId={profileId} selectedDate={selectedDate} eventTypeId={eventTypeId} />
        </div>
        <EditTarget
          isOpen={isTargetModalOpen}
          userName={userName}
          initialValues={targetData}
          onClose={() => setIsTargetModalOpen(false)}
          onSubmit={handleTargetSubmit}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default UserAnalytics;
